import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Sparkles, FileText } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogsManager = () => {
  const { toast } = useToast();
  const [blogs, setBlogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAutoGenModal, setShowAutoGenModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [generatingMeta, setGeneratingMeta] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [autoGenTopic, setAutoGenTopic] = useState('');
  const [autoGenWithImage, setAutoGenWithImage] = useState(true);
  const [generatingImage, setGeneratingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    featured_image: '',
    meta_title: '',
    meta_description: '',
    author: 'Admin',
    comments_enabled: true,
    status: 'draft'
  });
  
  const [imageStyle, setImageStyle] = useState('professional');
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(response.data);
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
      toast({ title: "Error", description: "Failed to fetch blogs", variant: "destructive" });
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleTitleChange = (value) => {
    setFormData(prev => ({
      ...prev,
      title: value,
      slug: generateSlug(value)
    }));
  };

  const handleGenerateMetaTags = async () => {
    if (!formData.title || !formData.content) {
      toast({ title: "Error", description: "Please enter title and content first", variant: "destructive" });
      return;
    }

    try {
      setGeneratingMeta(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API}/blogs/generate-meta-tags?title=${encodeURIComponent(formData.title)}&content=${encodeURIComponent(formData.content.substring(0, 500))}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setFormData(prev => ({
        ...prev,
        meta_title: response.data.meta_title,
        meta_description: response.data.meta_description
      }));
      
      toast({ title: "Success", description: "Meta tags generated successfully" });
    } catch (error) {
      console.error('Failed to generate meta tags:', error);
      toast({ title: "Error", description: "Failed to generate meta tags", variant: "destructive" });
    } finally {
      setGeneratingMeta(false);
    }
  };

  const handleAutoGenerateBlog = async () => {
    if (!autoGenTopic.trim()) {
      toast({ title: "Error", description: "Please enter a topic", variant: "destructive" });
      return;
    }

    try {
      setAutoGenerating(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API}/blogs/auto-generate?topic=${encodeURIComponent(autoGenTopic)}&generate_image=${autoGenWithImage}&image_style=${imageStyle}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000 // 2 minutes timeout for image generation
        }
      );
      
      const message = autoGenWithImage 
        ? "Blog and thumbnail generated successfully as draft! Please review and publish."
        : "Blog generated successfully as draft! Please review and publish.";
      
      toast({ 
        title: "Success", 
        description: message
      });
      
      setShowAutoGenModal(false);
      setAutoGenTopic('');
      setAutoGenWithImage(true); // Reset to default
      setImageStyle('professional'); // Reset to default
      fetchBlogs();
    } catch (error) {
      console.error('Failed to auto-generate blog:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to generate blog", 
        variant: "destructive" 
      });
    } finally {
      setAutoGenerating(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!formData.title.trim()) {
      toast({ title: "Error", description: "Please enter a blog title first", variant: "destructive" });
      return;
    }

    try {
      setGeneratingImage(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API}/blogs/generate-image?topic=${encodeURIComponent(formData.title)}&style=${imageStyle}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000 // 2 minutes timeout
        }
      );
      
      setFormData(prev => ({
        ...prev,
        featured_image: response.data.image_url
      }));
      
      toast({ 
        title: "Success", 
        description: `Thumbnail generated in ${imageStyle} style!` 
      });
    } catch (error) {
      console.error('Failed to generate image:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to generate image", 
        variant: "destructive" 
      });
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast({ 
        title: "Error", 
        description: "Invalid file type. Please upload JPG, PNG, WEBP, or GIF",
        variant: "destructive" 
      });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({ 
        title: "Error", 
        description: "File too large. Maximum size is 5MB",
        variant: "destructive" 
      });
      return;
    }

    try {
      setUploadingImage(true);
      const token = localStorage.getItem('admin_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({ ...prev, featured_image: response.data.url }));
      toast({ title: "Success", description: "Image uploaded successfully" });
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to upload image",
        variant: "destructive" 
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        toast({
          title: "Authentication Required",
          description: "Please log in to continue",
          variant: "destructive"
        });
        window.location.href = '/login';
        return;
      }
      
      if (editingBlog) {
        await axios.put(`${API}/blogs/${editingBlog.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Success", description: "Blog updated successfully" });
      } else {
        await axios.post(`${API}/blogs`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: "Success", description: "Blog created successfully" });
      }

      fetchBlogs();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Failed to save blog:', error);
      
      // Handle 401 Unauthorized specifically
      if (error.response?.status === 401) {
        toast({
          title: "Session Expired",
          description: "Your session has expired. Please log in again.",
          variant: "destructive"
        });
        setTimeout(() => {
          localStorage.removeItem('admin_token');
          window.location.href = '/login';
        }, 2000);
        return;
      }
      
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save blog",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (blogId) => {
    const confirmed = await window.confirm('Are you sure you want to delete this blog?');
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: "Blog deleted successfully" });
      fetchBlogs();
    } catch (error) {
      console.error('Failed to delete blog:', error);
      toast({ title: "Error", description: "Failed to delete blog", variant: "destructive" });
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      slug: blog.slug,
      content: blog.content,
      featured_image: blog.featured_image,
      meta_title: blog.meta_title,
      meta_description: blog.meta_description,
      author: blog.author,
      comments_enabled: blog.comments_enabled,
      status: blog.status
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingBlog(null);
    setFormData({
      title: '',
      slug: '',
      content: '',
      featured_image: '',
      meta_title: '',
      meta_description: '',
      author: 'Admin',
      comments_enabled: true,
      status: 'draft'
    });
  };

  return (
    <div className="admin-content">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Blogs Management</h1>
          <p className="body-medium">Create and manage blog posts</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => setShowAutoGenModal(true)} className="btn-secondary">
            <Sparkles size={18} />
            <span>Auto Generate Blog</span>
          </button>
          <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary">
            <Plus size={18} />
            <span>Add Blog</span>
          </button>
        </div>
      </div>

      <div className="content-card">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Featured Image</th>
                <th>Title</th>
                <th>Status</th>
                <th>Views</th>
                <th>Comments</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    <FileText size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                    <p className="body-large">No blogs found</p>
                    <p className="body-small">Click "Add Blog" to create your first blog post</p>
                  </td>
                </tr>
              ) : (
                blogs.map((blog) => (
                  <tr key={blog.id}>
                    <td style={{ width: '100px', padding: '8px' }}>
                      {blog.featured_image && !imageErrors[blog.id] ? (
                        <img 
                          src={blog.featured_image} 
                          alt={blog.title}
                          style={{ 
                            width: '80px', 
                            height: '60px', 
                            objectFit: 'cover', 
                            borderRadius: '4px',
                            display: 'block'
                          }}
                          onError={() => {
                            setImageErrors(prev => ({ ...prev, [blog.id]: true }));
                          }}
                        />
                      ) : (
                        <span style={{ color: '#999', fontSize: '12px' }}>No image</span>
                      )}
                    </td>
                    <td className="body-medium">{blog.title}</td>
                    <td>
                      <span className={`status-badge ${blog.status === 'published' ? 'active' : 'inactive'}`}>
                        {blog.status}
                      </span>
                    </td>
                    <td className="body-small">{blog.views_count}</td>
                    <td className="body-small">{blog.comments_enabled ? 'Enabled' : 'Disabled'}</td>
                    <td className="body-small">{new Date(blog.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="table-actions">
                        <button onClick={() => handleEdit(blog)} className="icon-btn" title="Edit">
                          <Edit2 size={18} />
                        </button>
                        <button onClick={() => handleDelete(blog.id)} className="icon-btn danger" title="Delete">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Blog Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content blog-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="heading-2">{editingBlog ? 'Edit Blog' : 'Add New Blog'}</h2>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Slug *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Content * (HTML supported)</label>
                <textarea
                  className="form-input"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows="12"
                  placeholder="Enter blog content (HTML tags supported: <p>, <h2>, <h3>, <ul>, <li>, <strong>, <em>, etc.)"
                  required
                />
                <p className="body-small" style={{ marginTop: '4px', color: '#64748b' }}>
                  Tip: You can use basic HTML tags for formatting
                </p>
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="form-label">Featured Image</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <select
                      value={imageStyle}
                      onChange={(e) => setImageStyle(e.target.value)}
                      disabled={generatingImage}
                      style={{ 
                        fontSize: '0.75rem', 
                        padding: '0.375rem 0.5rem',
                        borderRadius: '6px',
                        border: '1px solid var(--border-light)',
                        backgroundColor: 'var(--bg-card)',
                        color: 'var(--text-primary)'
                      }}
                    >
                      <option value="professional">Professional</option>
                      <option value="realistic">Realistic</option>
                      <option value="illustrative">Illustrative</option>
                      <option value="modern">Modern</option>
                    </select>
                    <button
                      type="button"
                      onClick={handleGenerateImage}
                      disabled={generatingImage || !formData.title.trim()}
                      className="btn-secondary"
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Sparkles size={14} />
                      <span>{generatingImage ? 'Generating...' : 'Generate with AI'}</span>
                    </button>
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    style={{ display: 'block', marginBottom: '8px' }}
                  />
                  <p className="body-small" style={{ color: '#64748b', marginBottom: '4px' }}>
                    <strong>Image Specifications:</strong> JPG, PNG, WEBP, or GIF | Max 5MB | Recommended: 1200x900px (4:3)
                  </p>
                  {uploadingImage && <p className="body-small" style={{ color: '#8b5cf6' }}>Uploading...</p>}
                  {generatingImage && (
                    <p className="body-small" style={{ color: '#8b5cf6', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div className="loading-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></div>
                      Generating {imageStyle} style image... This may take 30-60 seconds.
                    </p>
                  )}
                </div>
                <input
                  type="text"
                  className="form-input"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({...formData, featured_image: e.target.value})}
                  placeholder="/uploads/image.jpg or external URL"
                />
                {formData.featured_image && (
                  <div style={{ marginTop: '12px' }}>
                    <img 
                      src={formData.featured_image} 
                      alt="Preview" 
                      style={{ maxWidth: '300px', maxHeight: '225px', borderRadius: '8px', border: '1px solid var(--border-light)', objectFit: 'cover' }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="form-label">Meta Title</label>
                  <button
                    type="button"
                    onClick={handleGenerateMetaTags}
                    disabled={generatingMeta}
                    className="btn-secondary"
                    style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                  >
                    <Sparkles size={16} />
                    <span>{generatingMeta ? 'Generating...' : 'AI Generate'}</span>
                  </button>
                </div>
                <input
                  type="text"
                  className="form-input"
                  value={formData.meta_title}
                  onChange={(e) => setFormData({...formData, meta_title: e.target.value})}
                  placeholder="SEO-friendly title (50-60 characters)"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Meta Description</label>
                <textarea
                  className="form-input"
                  value={formData.meta_description}
                  onChange={(e) => setFormData({...formData, meta_description: e.target.value})}
                  placeholder="SEO-friendly description (150-160 characters)"
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Author</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.author}
                    onChange={(e) => setFormData({...formData, author: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-input"
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.comments_enabled}
                    onChange={(e) => setFormData({...formData, comments_enabled: e.target.checked})}
                  />
                  <span>Enable Comments</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingBlog ? 'Update Blog' : 'Create Blog'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Auto Generate Blog Modal */}
      {showAutoGenModal && (
        <div className="modal-overlay" onClick={() => setShowAutoGenModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2 className="heading-2" style={{ padding: '0 0 8px 0' }}>Auto Generate Blog with AI</h2>
            <p className="body-medium" style={{ marginBottom: '24px', color: '#64748b', lineHeight: '1.5' }}>
              Enter a topic and AI will create a complete blog post for you to review.
            </p>
            
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>Blog Topic *</label>
              <input
                type="text"
                className="form-input"
                value={autoGenTopic}
                onChange={(e) => setAutoGenTopic(e.target.value)}
                placeholder="e.g., Australia Work Visa Process 2024"
                disabled={autoGenerating}
                style={{ width: '100%' }}
              />
              <p className="body-small" style={{ marginTop: '8px', color: '#64748b', lineHeight: '1.4' }}>
                Be specific for better results. The blog will be created as a draft for your review.
              </p>
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={autoGenWithImage}
                  onChange={(e) => setAutoGenWithImage(e.target.checked)}
                  disabled={autoGenerating}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span className="body-medium">Generate thumbnail image with AI (recommended)</span>
              </label>
              <p className="body-small" style={{ marginTop: '4px', marginLeft: '26px', color: '#64748b', lineHeight: '1.4' }}>
                Creates a professional 4:3 thumbnail image optimized for blog cards.
              </p>
            </div>

            {autoGenWithImage && (
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>Image Style</label>
                <select
                  className="form-input"
                  value={imageStyle}
                  onChange={(e) => setImageStyle(e.target.value)}
                  disabled={autoGenerating}
                  style={{ width: '100%' }}
                >
                  <option value="professional">Professional - Clean corporate design</option>
                  <option value="realistic">Realistic - High-quality photography</option>
                  <option value="illustrative">Illustrative - Colorful vector art</option>
                  <option value="modern">Modern - Minimalist contemporary design</option>
                </select>
              </div>
            )}

            {autoGenerating && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div className="loading-spinner" style={{ margin: '0 auto 12px' }}></div>
                <p className="body-medium" style={{ color: '#64748b' }}>
                  {autoGenWithImage 
                    ? 'Generating blog and image... This may take 60-90 seconds.' 
                    : 'Generating your blog... This may take 30-60 seconds.'}
                </p>
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: '24px', paddingTop: '16px' }}>
              <button 
                type="button" 
                onClick={() => setShowAutoGenModal(false)} 
                className="btn-secondary"
                disabled={autoGenerating}
              >
                Cancel
              </button>
              <button 
                onClick={handleAutoGenerateBlog} 
                className="btn-primary"
                disabled={autoGenerating || !autoGenTopic.trim()}
              >
                <Sparkles size={18} />
                <span>{autoGenerating ? 'Generating...' : 'Generate Blog'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogsManager;
